import React from "react";

import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";

import validation from "core/validation";

import { Status } from "core/models/error";

import Icon from "ui/atom/Icon";
import Accordion from "ui/base/Accordion";
import Card from "ui/base/Card";
import Button from "ui/base/Button";
import Page from "ui/Page";

import api from "api";

import Box from "ui/atom/Box";


const getCopy = (error) => {
	let icon;
	let title;
	let text;

	switch (error) {
		case Status[401]: {
			icon = "lock";
			title = "Sign-in Required";
			text = "You need to sign in to access this page.";
		};
			break;
		case Status[403]: {
			icon = "shield-ban";
			title = "Access Denied";
			text = "You do not have permission to view this content.";
		};
			break;
		case Status[500]: {
			icon = "bug";
			title = "Server Error";
			text = "Something went wrong on our side. Please refresh this page or try again later.";
		}; break;
		default: {
			icon = "code";
			title = "Browser Error";
			text = "Something went wrong in the browser. Please refresh this web page.";
		}; break;
	}

	return { icon, title, text };
};

const initial = { hasError: false, error: null, errorInfo: null, clientError: false };

class ErrorBoundaryRaw extends React.Component {
	constructor(props) {
		super(props);

		this.state = initial;
		if (props.error) {
			this.state.hasError = true,
			this.state.error = validation.serializeError(props.error);
			this.state.clientError = !this.state.error?.type;
		}
	}
	static getDerivedStateFromError(error) {
		return { hasError: true, error };
	}

	componentDidCatch(error, errorInfo) {
		this.setState({ error, errorInfo });
		api.event.error(error);
	}

	reset() {
		this.setState = initial;
	}

	render() {
		const copy = getCopy(this.state.error?.type);
		const isProd = !import.meta.env.PROD;
		const notified = this.state.clientError || this.state.error?.type === Status[500];

		const style = {
			body: {
				height: "100%",
				alignItems: "center",
				justifyContent: "center"
			},
			card: {
				maxWidth: "400px",
				width: "100%"
			},
			content: {
				alignItems: "center",
				textAlign: "center"
			},
			details: {
				width: "100%"
			},
			stack: {
				whiteSpace: "pre",
				fontFamily: "monospace",
				textAlign: "left",
				fontSize: 14,
				overflow: "auto"
			}
		};

		if (this.state.hasError) return (
			<Page>
				<Page.Body sx={style.body}>
					<Card raised sx={style.card}>
						<Card.Content sx={style.content}>
							<Icon name={copy.icon} size={50} color={this.props.theme.palette.error.main} />
							<Typography variant="h2">{copy.title}</Typography>
							<Typography>{copy.text}</Typography>
							{isProd
								? notified && <Typography variant="h6">Our engineers have been notified about this issue. Stay tuned...</Typography>
								: (
									<Accordion sx={style.details}>
										<Accordion.Summary slotProps={{ root: { sx: { fontSize: 18, fontWeight: "700" } } }}>{this.state.error.message}</Accordion.Summary>
										<Accordion.Details sx={style.stack}>
											{this.state.error.stack	}
										</Accordion.Details>
									</Accordion>
								)}
							<Box gap={2}>
								{!isProd && <Button.Clipboard variant="contained" color="error" value={JSON.stringify(this.state.error, null, 2)} icon={false}>Copy Error</Button.Clipboard>}
								<Button.Link to="/" variant="outlined" startIcon={<Icon name="house" size={18} />} onClick={this.reset}>Home</Button.Link>
							</Box>
						</Card.Content>
					</Card>
				</Page.Body>
			</Page>
		);

		return this.props.children;
	}
}

export default function ErrorBoundary(props) {
	const theme = useTheme();
	return <ErrorBoundaryRaw {...props} theme={theme} />;
}